import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { useState, useCallback, useMemo } from "react";
import { View, Text, ScrollView, Platform } from "react-native";
import {
  Activity,
  Ban,
  Brain,
  CircleHelp,
  Dna,
  HeartPulse,
  LockKeyhole,
  Pill,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UserRound,
  Users,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  type ContentBlock,
  type ContentBlockId,
} from "@src/packages/content";
import { useFertilityIntent } from "@src/packages/fertility-intent";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import { MoonFilterChip, MoonSearchInput, BackLink } from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import AccessGridCard from "../../components/access/AccessGridCard";
import StatusBadge from "../../components/StatusBadge";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { colors } from "@louise/theme/tokens";

type SupplementGoal =
  | "egg-quality"
  | "sperm-health"
  | "pcos-support"
  | "pre-ivf"
  | "implantation"
  | "stress-support"
  | "antioxidant"
  | "general-wellness";

type SupplementsFilters = {
  forWhom?: ("her" | "him" | "both")[];
  goals?: SupplementGoal[];
};

type EvidenceStrength = ContentBlock<"supplement">["body"]["evidenceStrength"];

const EVIDENCE_ICONS: Record<EvidenceStrength, LucideIcon> = {
  strong: ShieldCheck,
  moderate: TrendingUp,
  emerging: Sparkles,
  mixed: CircleHelp,
  debunked: Ban,
};

const FOR_WHOM_OPTIONS: {
  labelKey: string;
  value: "her" | "him" | "both";
  icon: LucideIcon;
}[] = [
  {
    labelKey: "access.supplements.for_whom.her",
    value: "her",
    icon: HeartPulse,
  },
  {
    labelKey: "access.supplements.for_whom.him",
    value: "him",
    icon: UserRound,
  },
  { labelKey: "access.supplements.for_whom.both", value: "both", icon: Users },
];

const GOAL_OPTIONS: {
  labelKey: string;
  value: SupplementGoal;
  icon: LucideIcon;
}[] = [
  {
    labelKey: "access.supplements.goal.egg_quality",
    value: "egg-quality",
    icon: Sparkles,
  },
  {
    labelKey: "access.supplements.goal.sperm_health",
    value: "sperm-health",
    icon: Activity,
  },
  {
    labelKey: "access.supplements.goal.pcos_support",
    value: "pcos-support",
    icon: HeartPulse,
  },
  {
    labelKey: "access.supplements.goal.pre_ivf",
    value: "pre-ivf",
    icon: ShieldCheck,
  },
  {
    labelKey: "access.supplements.goal.implantation",
    value: "implantation",
    icon: Dna,
  },
  {
    labelKey: "access.supplements.goal.stress_support",
    value: "stress-support",
    icon: Brain,
  },
  {
    labelKey: "access.supplements.goal.antioxidant",
    value: "antioxidant",
    icon: Sparkles,
  },
  {
    labelKey: "access.supplements.goal.general_wellness",
    value: "general-wellness",
    icon: Users,
  },
];

const GOAL_SUPPLEMENTS: Record<SupplementGoal, ContentBlockId[]> = {
  "egg-quality": [
    "supplement-coq10",
    "supplement-vitamin-d",
    "supplement-vitamin-e",
    "supplement-melatonin",
    "supplement-dhea",
    "supplement-methylfolate",
  ] as ContentBlockId[],
  "sperm-health": [
    "supplement-zinc",
    "supplement-l-carnitine",
    "supplement-selenium",
    "supplement-vitamin-e",
    "supplement-omega-3",
    "supplement-vitamin-c",
  ] as ContentBlockId[],
  "pcos-support": [
    "supplement-myo-inositol",
    "supplement-nac",
    "supplement-vitamin-d",
  ] as ContentBlockId[],
  "pre-ivf": [
    "supplement-coq10",
    "supplement-dhea",
    "supplement-melatonin",
    "supplement-methylfolate",
    "supplement-vitamin-d",
    "supplement-choline",
    "supplement-iodine",
  ] as ContentBlockId[],
  antioxidant: [
    "supplement-nac",
    "supplement-vitamin-c",
    "supplement-vitamin-e",
    "supplement-coq10",
    "supplement-selenium",
  ] as ContentBlockId[],
  implantation: [
    "supplement-l-arginine",
    "supplement-vitamin-e",
    "supplement-vitamin-d",
    "supplement-probiotics",
    "supplement-vitamin-b-complex",
    "supplement-iron",
  ] as ContentBlockId[],
  "stress-support": [
    "supplement-ashwagandha",
    "supplement-magnesium",
    "supplement-vitamin-b-complex",
    "supplement-omega-3",
    "supplement-melatonin",
  ] as ContentBlockId[],
  "general-wellness": [
    "supplement-omega-3",
    "supplement-probiotics",
    "supplement-magnesium",
    "supplement-vitamin-d",
    "supplement-iron",
    "supplement-vitamin-c",
    "supplement-choline",
    "supplement-iodine",
    "supplement-calcium",
  ] as ContentBlockId[],
};

const supplementStackHeroBg =
  require("../../../assets/images/care-hero-glow-gradient.png") as number;

const SUPPLEMENT_BOTTLES = [
  require("../../../assets/images/supplement-bottle.png"),
  require("../../../assets/images/supplement-bottle-2.png"),
  require("../../../assets/images/supplement-bottle-3.png"),
];

/** Stable bottle index from supplement ID — must match SupplementDetailSheet */
function bottleIndex(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % SUPPLEMENT_BOTTLES.length;
}

const EVIDENCE_LABEL_KEYS: Record<string, string> = {
  strong: "access.supplements.evidence.strong",
  moderate: "access.supplements.evidence.moderate",
  emerging: "access.supplements.evidence.emerging",
  mixed: "access.supplements.evidence.mixed",
  debunked: "access.supplements.evidence.debunked",
};

/** Map fertility intent to recommended supplement IDs */
function getRecommendedIds(
  reasons: string[],
  conditions: string[],
  hasVaricocele: boolean,
): Set<ContentBlockId> {
  const ids = new Set<ContentBlockId>();
  // Diminished ovarian reserve and primary ovarian insufficiency both reflect
  // reduced ovarian reserve and qualify for the same egg-quality support.
  const hasLowOvarianReserve =
    conditions.includes("diminished_ovarian_reserve") ||
    conditions.includes("primary_ovarian_insufficiency");
  // AMAB disease conditions or a varicocele diagnosis qualify for male-factor support.
  const hasMaleFactor =
    conditions.includes("klinefelter_syndrome") ||
    conditions.includes("y_chromosome_microdeletions") ||
    hasVaricocele;
  // CoQ10 + Methylfolate for anyone planning a cycle or preserving
  if (
    reasons.includes("more_time") ||
    reasons.includes("no_partner") ||
    reasons.includes("ivf_planned")
  ) {
    ids.add("supplement-coq10" as ContentBlockId);
    ids.add("supplement-methylfolate" as ContentBlockId);
  }
  // Vitamin D is recommended for most people, especially with conditions
  if (
    hasLowOvarianReserve ||
    conditions.includes("pcos") ||
    conditions.includes("endometriosis")
  ) {
    ids.add("supplement-vitamin-d" as ContentBlockId);
    ids.add("supplement-coq10" as ContentBlockId);
  }
  // Myo-Inositol + NAC for PMOS (ex PCOS)
  if (conditions.includes("pcos")) {
    ids.add("supplement-myo-inositol" as ContentBlockId);
    ids.add("supplement-nac" as ContentBlockId);
  }
  // DHEA for diminished ovarian reserve / primary ovarian insufficiency
  if (hasLowOvarianReserve) {
    ids.add("supplement-dhea" as ContentBlockId);
  }
  // Melatonin for IVF
  if (reasons.includes("ivf_planned")) {
    ids.add("supplement-melatonin" as ContentBlockId);
  }
  // NAC for endometriosis
  if (conditions.includes("endometriosis")) {
    ids.add("supplement-nac" as ContentBlockId);
  }
  // Male factor: Omega-3, Zinc, L-Carnitine, Selenium, Vitamin E
  if (hasMaleFactor) {
    ids.add("supplement-omega-3" as ContentBlockId);
    ids.add("supplement-zinc" as ContentBlockId);
    ids.add("supplement-l-carnitine" as ContentBlockId);
    ids.add("supplement-selenium" as ContentBlockId);
    ids.add("supplement-vitamin-e" as ContentBlockId);
  }
  return ids;
}

function SupplementStackHeroCard({
  recommendedCount,
  showingStack,
  onToggleStack,
  t,
}: {
  recommendedCount: number;
  showingStack: boolean;
  onToggleStack: () => void;
  t: TranslationFunction;
}) {
  const hasRecommendations = recommendedCount > 0;
  const buttonLabel = showingStack
    ? t("access.supplements.hero.cta_show_all")
    : hasRecommendations
      ? t("access.supplements.hero.cta_view_stack")
      : t("access.supplements.hero.cta_unlock");

  return (
    <Hero
      style={{ marginTop: 16 }}
      backgroundSource={supplementStackHeroBg}
      eyebrow={{ label: t("access.supplements.hero.eyebrow"), icon: Pill }}
      title={t("access.supplements.hero.title")}
      summary={
        hasRecommendations
          ? t("access.supplements.hero.with_count", {
              params: { count: String(recommendedCount) },
            })
          : t("access.supplements.hero.without_count")
      }
      primaryAction={{
        label: buttonLabel,
        icon: hasRecommendations ? undefined : LockKeyhole,
        onPress: onToggleStack,
      }}
    />
  );
}

type Props = NativeStackScreenProps<AccessStackParamList, "Supplements">;

export default function SupplementsScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<SupplementsFilters>(() =>
    route.params?.goal ? { goals: [route.params.goal] } : {},
  );
  const [showRecommendedOnly, setShowRecommendedOnly] = useState(false);

  const { data: fertilityIntent } = useFertilityIntent();
  const { data: patient } = usePatient();

  const supplements = useMemo(() => getBlocksByKind("supplement"), []);

  const recommendedIds = useMemo(() => {
    if (!fertilityIntent) return new Set<ContentBlockId>();
    const reasons = fertilityIntent.preservationReasons ?? [];
    const conditions = (patient?.medicalHistory?.diseases ?? [])
      .map((d) => d.disease ?? "")
      .filter((d) => !!d);
    const hadVaricoceles = patient?.medicalHistory?.hadVaricoceles;
    const hasVaricocele =
      hadVaricoceles === "yes_diagnosed" ||
      hadVaricoceles === "yes_undiagnosed";
    if (reasons.length === 0 && conditions.length === 0 && !hasVaricocele) {
      return new Set<ContentBlockId>();
    }
    return getRecommendedIds(reasons, conditions, hasVaricocele);
  }, [fertilityIntent, patient]);

  const filtered = useMemo(() => {
    let result = supplements;

    if (showRecommendedOnly && recommendedIds.size > 0) {
      result = result.filter((s) => recommendedIds.has(s.id));
    }

    // Text search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (s) =>
          s.body.name.toLowerCase().includes(q) ||
          s.title.toLowerCase().includes(q),
      );
    }

    // For whom filter
    if (filters.forWhom?.length) {
      result = result.filter((s) => {
        const whom = s.body.forWhom;
        // "both" supplements show when her or him is selected
        if (whom === "both") return true;
        return filters.forWhom!.includes(whom as "her" | "him" | "both");
      });
    }

    // Goal filter — show supplements that match ANY selected goal
    if (filters.goals?.length) {
      const goalIds = new Set<ContentBlockId>();
      for (const goal of filters.goals) {
        for (const id of GOAL_SUPPLEMENTS[goal]) {
          goalIds.add(id);
        }
      }
      result = result.filter((s) => goalIds.has(s.id));
    }

    return result;
  }, [supplements, search, filters, showRecommendedOnly, recommendedIds]);

  const onItemPress = useCallback(
    (item: ContentBlock<"supplement">) => {
      navigation.navigate("SupplementDetail", { supplementId: item.id });
    },
    [navigation],
  );

  const handleToggleRecommendedStack = useCallback(() => {
    if (recommendedIds.size === 0) {
      return;
    }
    setSearch("");
    setFilters({});
    setShowRecommendedOnly((current) => !current);
  }, [recommendedIds.size]);

  const toggleForWhom = useCallback((value: "her" | "him" | "both") => {
    setShowRecommendedOnly(false);
    setFilters((prev) => {
      const current = prev.forWhom ?? [];
      const has = current.includes(value);
      return {
        ...prev,
        forWhom: has ? current.filter((v) => v !== value) : [...current, value],
      };
    });
  }, []);

  const toggleGoal = useCallback((value: SupplementGoal) => {
    setShowRecommendedOnly(false);
    setFilters((prev) => {
      const current = prev.goals ?? [];
      const has = current.includes(value);
      return {
        ...prev,
        goals: has
          ? current.filter((goal) => goal !== value)
          : [...current, value],
      };
    });
  }, []);

  // Pair items into rows of 2
  const rows = useMemo(() => {
    const result: ContentBlock<"supplement">[][] = [];
    for (let i = 0; i < filtered.length; i += 2) {
      result.push(filtered.slice(i, i + 2));
    }
    return result;
  }, [filtered]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <BackLink
          label={
            route.params?.returnTab ? t("you.shared.back") : t("access.back")
          }
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: colors.deepBrown,
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          {t("access.supplements.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
        >
          {t("access.supplements.subtitle")}
        </Text>

        <MoonSearchInput
          placeholder={t("access.supplements.search_placeholder")}
          value={search}
          onChangeText={setSearch}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingTop: 14,
          }}
        >
          {FOR_WHOM_OPTIONS.map((option) => (
            <MoonFilterChip
              key={option.value}
              label={t(option.labelKey)}
              icon={option.icon}
              selected={filters.forWhom?.includes(option.value) ?? false}
              onPress={() => toggleForWhom(option.value)}
              tone="glass"
              showCheck={false}
            />
          ))}
          {GOAL_OPTIONS.map((option) => (
            <MoonFilterChip
              key={option.value}
              label={t(option.labelKey)}
              icon={option.icon}
              selected={filters.goals?.includes(option.value) ?? false}
              onPress={() => toggleGoal(option.value)}
              tone="glass"
              showCheck={false}
            />
          ))}
        </ScrollView>

        <SupplementStackHeroCard
          recommendedCount={recommendedIds.size}
          showingStack={showRecommendedOnly}
          onToggleStack={handleToggleRecommendedStack}
          t={t}
        />

        {/* 2x2 Grid */}
        <View style={{ gap: 12, marginTop: 16 }}>
          {rows.map((row, rowIdx) => (
            <View key={rowIdx} style={{ flexDirection: "row", gap: 12 }}>
              {row.map((supplement) => {
                const { body } = supplement;
                const benefit = body.expectations?.canExpect[0] ?? "";
                const isRecommended = recommendedIds.has(supplement.id);
                const evidenceLabel = EVIDENCE_LABEL_KEYS[body.evidenceStrength]
                  ? t(EVIDENCE_LABEL_KEYS[body.evidenceStrength]!)
                  : body.evidenceStrength;
                const EvidenceIcon = EVIDENCE_ICONS[body.evidenceStrength];

                return (
                  <AccessGridCard
                    key={supplement.id}
                    title={body.name}
                    description={benefit}
                    imageSource={
                      SUPPLEMENT_BOTTLES[bottleIndex(supplement.id as string)]
                    }
                    imageStyle={{ width: 64, height: 82 }}
                    onPress={() => onItemPress(supplement)}
                    tags={
                      <>
                        {isRecommended ? (
                          <StatusBadge
                            label={t("access.supplements.for_you")}
                            tone="neutral"
                            shape="pill"
                            icon={UserRound}
                          />
                        ) : null}
                        <StatusBadge
                          label={evidenceLabel}
                          tone="neutral"
                          shape="pill"
                          icon={EvidenceIcon}
                        />
                      </>
                    }
                  />
                );
              })}
              {/* Spacer if odd number of items in last row */}
              {row.length === 1 && <View style={{ flex: 1 }} />}
            </View>
          ))}
        </View>

        {/* Empty state */}
        {filtered.length === 0 && (
          <SearchEmptyState
            title={t("access.supplements.empty")}
            onReset={() => {
              setSearch("");
              setFilters({});
              setShowRecommendedOnly(false);
            }}
          />
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
